import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DBFile, DBFolder, fileTypeIcon, fileTypeColor, getFileExt, formatBytes, formatDate, isImageType } from './filesTypes';
import { FILE_AUDIENCE_VALUES, getAudienceBadgeClass, getAudienceLabel, resolveAudienceFromRecord, type FileAudience } from '@/lib/fileAudience';

interface FileRowProps {
  file: DBFile;
  displayUrl?: string;
  canUpload: boolean;
  folders: DBFolder[];
  onOpenImage: (url: string) => void;
  onOpenPdf: (file: DBFile) => void;
  onOpenOther: (file: DBFile) => void;
  onDelete: (id: string) => void;
  onToggleVisibility: (id: string, visible: boolean) => void;
  onMoveFolder: (fileId: string, folderId: string | null) => void;
  onChangeAudience: (fileId: string, audience: FileAudience) => void;
}

export default function FileRow({ file, displayUrl, canUpload, folders, onOpenImage, onOpenPdf, onOpenOther, onDelete, onToggleVisibility, onMoveFolder, onChangeAudience }: FileRowProps) {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const ext = getFileExt(file.file_name);
  const isImage = isImageType(ext);
  const iconKey = fileTypeIcon[ext] ? ext : 'other';
  const currentFolder = folders.find((f) => f.id === file.folder_id);
  const audience = resolveAudienceFromRecord(file);

  const handleRowClick = () => {
    if (isImage) onOpenImage(displayUrl || file.file_url);
    else if (ext === 'pdf') onOpenPdf(file);
    else onOpenOther(file);
  };
  const handleDelete = () => {
    onDelete(file.id);
    setShowDeleteConfirm(false);
    setShowMenu(false);
  };
  const handleToggleVisibility = () => {
    onToggleVisibility(file.id, file.is_visible !== false);
    setShowMenu(false);
  };
  const handleMove = (folderId: string | null) => {
    onMoveFolder(file.id, folderId);
    setShowMoveMenu(false);
  };

  return (
    <div className="relative">
      <div onClick={handleRowClick} className="px-4 py-3 sm:px-5 sm:py-4 cursor-pointer hover:bg-gray-50 transition-colors">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${fileTypeColor[iconKey] || '#9CA3AF'}15` }}>
            <i className={`${fileTypeIcon[iconKey] || 'ri-file-line'} text-lg`} style={{ color: fileTypeColor[iconKey] || '#9CA3AF' }}></i>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[#374151] text-sm font-medium truncate max-w-[60%] sm:max-w-[70%]">{file.file_name}</p>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${getAudienceBadgeClass(audience)}`}>{getAudienceLabel(audience)}</span>
              {file.is_visible === false && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-[#9CA3AF] flex-shrink-0">{t('fileRow.hidden')}</span>}
              {currentFolder && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: `${currentFolder.color}15`, color: currentFolder.color }}>{currentFolder.name}</span>}
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-[#9CA3AF] text-xs">{formatBytes(file.file_size)}</span>
              <span className="text-[#D1D5DB] text-xs">·</span>
              <span className="text-[#9CA3AF] text-xs">{formatDate(file.created_at)}</span>
              {file.note && <><span className="text-[#D1D5DB] text-xs">·</span><span className="text-[#9CA3AF] text-xs truncate max-w-[120px]">{file.note}</span></>}
              {canUpload && (
                <select
                  aria-label="File audience"
                  value={audience}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => { e.stopPropagation(); onChangeAudience(file.id, e.target.value as FileAudience); }}
                  className="ml-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[10px] font-semibold text-[#374151] focus:outline-none focus:border-[#E8621A]"
                >
                  {FILE_AUDIENCE_VALUES.map((value) => <option key={value} value={value}>{getAudienceLabel(value)}</option>)}
                </select>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={(e) => { e.stopPropagation(); handleRowClick(); }} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-[#9CA3AF]"><i className="ri-eye-line text-lg"></i></button>
            {canUpload && (
              <div className="relative">
                <button onClick={(e) => { e.stopPropagation(); setShowMenu((v) => !v); setShowMoveMenu(false); }} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-[#9CA3AF]"><i className="ri-more-2-fill text-lg"></i></button>
                {showMenu && <>
                  <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setShowMenu(false); }} />
                  <div className="absolute right-0 top-10 z-50 bg-white rounded-xl border border-gray-100 overflow-hidden min-w-[180px] max-w-[calc(100vw-2rem)] shadow-sm">
                    <button onClick={(e) => { e.stopPropagation(); setShowMenu(false); setShowMoveMenu(true); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-[#374151] hover:bg-gray-50 cursor-pointer text-left whitespace-nowrap"><i className="ri-folder-transfer-line text-base"></i>{t('fileRow.moveToFolder')}</button>
                    <button onClick={(e) => { e.stopPropagation(); handleToggleVisibility(); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-[#374151] hover:bg-gray-50 cursor-pointer text-left whitespace-nowrap"><i className={`${file.is_visible === false ? 'ri-eye-line' : 'ri-eye-off-line'} text-base`}></i>{file.is_visible === false ? t('fileRow.unhide') : t('fileRow.hide')}</button>
                    <button onClick={(e) => { e.stopPropagation(); setShowMenu(false); setShowDeleteConfirm(true); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-[#EF4444] hover:bg-[#FEF2F2] cursor-pointer text-left whitespace-nowrap"><i className="ri-delete-bin-line text-base"></i>{t('fileRow.delete')}</button>
                  </div>
                </>}
                {showMoveMenu && <>
                  <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setShowMoveMenu(false); }} />
                  <div className="absolute right-0 top-10 z-50 bg-white rounded-xl border border-gray-100 overflow-hidden min-w-[200px] max-w-[calc(100vw-2rem)] max-h-[280px] overflow-y-auto shadow-sm">
                    <div className="px-3 py-2 border-b border-gray-50 flex items-center gap-1.5"><button onClick={(e) => { e.stopPropagation(); setShowMoveMenu(false); setShowMenu(true); }} className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 cursor-pointer text-[#9CA3AF]"><i className="ri-arrow-left-s-line text-base"></i></button><span className="text-[#1A1A1A] text-xs font-semibold">{t('fileRow.moveToFolder')}</span></div>
                    <button onClick={(e) => { e.stopPropagation(); handleMove(null); }} className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm cursor-pointer text-left whitespace-nowrap ${file.folder_id === null ? 'bg-[#FEF0E7] text-[#E8621A] font-medium' : 'text-[#374151] hover:bg-gray-50'}`}><i className="ri-folder-line text-base"></i>{t('fileRow.noFolder')}</button>
                    {folders.map((folder) => <button key={folder.id} onClick={(e) => { e.stopPropagation(); handleMove(folder.id); }} className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm cursor-pointer text-left whitespace-nowrap ${file.folder_id === folder.id ? 'bg-[#FEF0E7] text-[#E8621A] font-medium' : 'text-[#374151] hover:bg-gray-50'}`}><i className={`${folder.icon} text-base`} style={{ color: folder.color }}></i>{folder.name}</button>)}
                  </div>
                </>}
              </div>
            )}
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative bg-white w-full max-w-sm mx-4 rounded-2xl overflow-hidden p-5">
            <div className="w-12 h-12 rounded-full bg-[#FEF2F2] flex items-center justify-center mx-auto mb-3"><i className="ri-delete-bin-line text-[#EF4444] text-xl"></i></div>
            <p className="text-[#1A1A1A] font-bold text-base text-center mb-1">{t('fileRow.deleteFile')}</p>
            <p className="text-[#9CA3AF] text-sm text-center mb-5">{t('fileRow.deleteConfirm')}</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 py-3 rounded-xl bg-gray-100 text-[#374151] text-sm font-semibold cursor-pointer">{t('fileRow.cancel')}</button>
              <button onClick={handleDelete} className="flex-1 py-3 rounded-xl bg-[#EF4444] text-white text-sm font-semibold cursor-pointer">{t('fileRow.delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
